// src/pages/OrderCreatePage.js
import React, { useState, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Alert,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextareaAutosize,
} from "@mui/material";
import {
  PhotoCamera,
  Upload,
  Close,
  Add,
  Delete,
  Person,
  Phone,
  LocalHospital,
  Receipt,
  ShoppingCartCheckout,
  Warning,
  CheckCircle,
  Spa,
  AssignmentTurnedIn, // For bypass reasons
  MedicalInformation, // For prescription info
} from '@mui/icons-material';
import MedicineSearchModal from "../components/MedicineSearchModal";
import { createOrder, settingsApi } from "../services/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// ✅ Add translation hook
import { useTranslation } from 'react-i18next';

const steps = ['order.customerInfo', 'order.addMedicines', 'order.reviewComplete'];

// ✅ Predefined bypass reasons
const BYPASS_REASONS = [
  { id: 'emergency', label: 'order.bypassReason.emergency', description: 'order.bypassReason.emergencyDesc' },
  { id: 'regular_customer', label: 'order.bypassReason.regularCustomer', description: 'order.bypassReason.regularCustomerDesc' },
  { id: 'doctor_verbal', label: 'order.bypassReason.doctorVerbal', description: 'order.bypassReason.doctorVerbalDesc' },
  { id: 'temporary_supply', label: 'order.bypassReason.temporarySupply', description: 'order.bypassReason.temporarySupplyDesc' },
  { id: 'previous_prescription', label: 'order.bypassReason.previousPrescription', description: 'order.bypassReason.previousPrescriptionDesc' },
  { id: 'other', label: 'order.bypassReason.other', description: 'order.bypassReason.otherDesc' },
];

export default function OrderCreatePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeStep, setActiveStep] = useState(0);

  // 👤 Walk-in customer info
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  // 📸 Prescription Photo
  const [prescriptionPhoto, setPrescriptionPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  // ✅ Prescription Bypass
  const [bypassDialogOpen, setBypassDialogOpen] = useState(false);
  const [bypassReason, setBypassReason] = useState("");
  const [bypassCustomReason, setBypassCustomReason] = useState("");
  const [bypassApprovedBy, setBypassApprovedBy] = useState("");
  const [bypassNotes, setBypassNotes] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [cosmeticsTaxRate, setCosmeticsTaxRate] = useState(0.15); // Default to 15%
  const navigate = useNavigate();
  const theme = useTheme();

  // ✅ Fetch tax settings on load
  React.useEffect(() => {
    const fetchTaxSettings = async () => {
      try {
        const response = await settingsApi.getTax();
        const settings = response.data?.settings;
        if (settings) {
          const cosmeticsRate = settings.find(s => s.key === 'COSMETICS_TAX_RATE');
          if (cosmeticsRate) setCosmeticsTaxRate(parseFloat(cosmeticsRate.value));
        }
      } catch (err) {
        console.error("Failed to fetch tax settings:", err);
      }
    };
    fetchTaxSettings();
  }, []);

  // Refs for file inputs
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // ✅ Initialize translation
  const { t } = useTranslation();

  // ✅ Check if any selected medicine requires prescription
  const requiresPrescription = items.some(item => item.requiresPrescription);

  // ✅ Check if prescription is bypassed
  const isPrescriptionBypassed = bypassReason !== "";

  // ✅ Get category icon based on medicine category
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'prescription': return <Warning color="error" />;
      case 'OTC': return <LocalHospital color="success" />;
      case 'supplement': return <LocalHospital color="warning" />;
      case 'Cosmetics': return <Spa color="info" />;
      default: return <LocalHospital />;
    }
  };

  // ✅ Get category chip color
  const getCategoryColor = (category) => {
    switch (category) {
      case 'prescription': return 'error';
      case 'OTC': return 'success';
      case 'supplement': return 'warning';
      case 'Cosmetics': return 'info';
      default: return 'default';
    }
  };

  // ✅ Add medicine with default quantity = 1
  const handleAddMedicine = (medicine) => {
    if (!medicine || !medicine.id || typeof medicine.name !== 'string') {
      toast.error(t('common.invalidMedicineData'));
      return;
    }

    const price = parseFloat(medicine.price);
    if (isNaN(price)) {
      toast.error(t('common.invalidPrice', { name: medicine.name }));
      return;
    }

    // Check if medicine already exists
    const existingIndex = items.findIndex(item => item.medicineId === medicine.id);
    if (existingIndex >= 0) {
      // Update quantity if exists
      const updatedItems = [...items];
      updatedItems[existingIndex].quantity += 1;
      setItems(updatedItems);
      toast.success(t('common.quantityIncreased', { name: medicine.name }));
    } else {
      // Add new medicine with category
      setItems((prev) => [
        ...prev,
        {
          medicineId: medicine.id,
          name: medicine.name,
          price,
          quantity: 1,
          requiresPrescription: !!medicine.requiresPrescription,
          category: medicine.category || 'OTC'
        },
      ]);
      toast.success(t('common.addedMedicine', { name: medicine.name }));
    }
  };

  // ✅ Remove medicine by index
  const handleRemoveMedicine = (index) => {
    const medicineName = items[index].name;
    setItems((prev) => prev.filter((_, i) => i !== index));
    toast.info(t('common.removedMedicine', { name: medicineName }));
  };

  // ✅ Update quantity
  const handleQuantityChange = (index, newQty) => {
    if (newQty === '' || isNaN(newQty)) {
      const updated = [...items];
      updated[index].quantity = '';
      setItems(updated);
      return;
    }

    const qty = parseInt(newQty, 10);
    if (qty < 1) return;

    const updated = [...items];
    updated[index].quantity = qty;
    setItems(updated);
  };

  // ✅ Calculate total
  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.price * (item.quantity || 0), 0);
  };

  // ✅ Calculate subtotal
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.price * (item.quantity || 0), 0);
  };

  // ✅ Check if any item is Cosmetics
  const hasCosmetics = items.some(item => item.category === 'Cosmetics');

  // ✅ Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('common.imageOnly'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('common.imageTooLarge'));
      return;
    }

    setPrescriptionPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    toast.success(t('common.photoUploaded'));
  };

  // ✅ Open camera or gallery
  const openCamera = () => cameraInputRef.current?.click();
  const openGallery = () => galleryInputRef.current?.click();

  // ✅ Clear photo
  const clearPhoto = () => {
    setPrescriptionPhoto(null);
    setPhotoPreview("");
    toast.info(t('common.photoRemoved'));
  };

  // ✅ Clear bypass
  const clearBypass = () => {
    setBypassReason("");
    setBypassCustomReason("");
    setBypassApprovedBy("");
    setBypassNotes("");
    toast.info(t('order.bypassCleared'));
  };

  // ✅ Open bypass dialog
  const openBypassDialog = () => {
    setBypassDialogOpen(true);
  };

  // ✅ Submit bypass
  const handleBypassSubmit = () => {
    if (!bypassReason) {
      toast.error(t('order.selectBypassReason'));
      return;
    }

    if (bypassReason === 'other' && !bypassCustomReason.trim()) {
      toast.error(t('order.enterCustomReason'));
      return;
    }

    toast.success(t('order.bypassApproved'));
    setBypassDialogOpen(false);
  };

  // ✅ Navigation between steps
  const handleNext = () => {
    if (activeStep === 0 && (!customerName.trim() && !customerPhone.trim())) {
      toast.info(t('order.optionalCustomerDetails'));
    }

    if (activeStep === 1 && items.length === 0) {
      toast.error(t('order.addAtLeastOneMedicine'));
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // ✅ Submit order - FIXED VERSION
  const handleSubmit = async () => {
    if (items.length === 0) {
      setError(t('order.atLeastOneMedicine'));
      return;
    }

    // Validate items
    for (let item of items) {
      if (!item.medicineId || !item.quantity || item.quantity < 1) {
        toast.error(t('order.validQuantityRequired'));
        return;
      }
    }

    // Enforce prescription photo or bypass
    if (requiresPrescription && !prescriptionPhoto && !isPrescriptionBypassed) {
      const shouldUpload = window.confirm(t('order.noPrescriptionAlert'));
      if (shouldUpload) {
        const prescriptionSection = document.getElementById('prescription-section');
        if (prescriptionSection) {
          prescriptionSection.scrollIntoView({ behavior: 'smooth' });
        }
        return;
      } else {
        openBypassDialog();
        return;
      }
    }

    // ✅ Create FormData with CORRECT field names
    const formData = new FormData();

    // 1. Items should be sent as JSON string - FIXED: removed duplicate line
    const orderItems = items.map(item => ({
      medicineId: item.medicineId,
      quantity: Number(item.quantity)
    }));

    formData.append("items", JSON.stringify(orderItems));

    // 2. Customer info
    formData.append("customerName", customerName.trim() || t('order.walkInCustomer'));
    formData.append("customerPhone", customerPhone.trim() || "");

    // 3. Status
    formData.append("status", "completed");

    // 4. Prescription handling
    if (requiresPrescription) {
      if (prescriptionPhoto) {
        formData.append("prescriptionPhoto", prescriptionPhoto);
      } else if (isPrescriptionBypassed) {
        // These field names MUST match what backend expects
        formData.append("prescriptionBypassed", "true"); // Send as string "true"
        formData.append("bypassReason", bypassReason); // e.g., "emergency"

        if (bypassApprovedBy) {
          formData.append("bypassApprovedBy", bypassApprovedBy);
        }

        if (bypassNotes) {
          formData.append("bypassNotes", bypassNotes);
        }

        console.log("📤 Sending bypass data:", {
          prescriptionBypassed: "true",
          bypassReason,
          bypassApprovedBy,
          bypassNotes
        });
      }
    }

    setLoading(true);
    setError("");

    try {
      console.log("📤 DEBUG - FormData being sent:");
      console.log("Items:", orderItems);
      console.log("Customer Name:", customerName);
      console.log("Requires Prescription:", requiresPrescription);
      console.log("Has Prescription Photo:", !!prescriptionPhoto);
      console.log("Is Prescription Bypassed:", isPrescriptionBypassed);
      console.log("Bypass Reason:", bypassReason);

      // ✅ Log all FormData entries
      for (let [key, value] of formData.entries()) {
        if (key === 'prescriptionPhoto') {
          console.log(`${key}: File - ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`${key}:`, value);
        }
      }

      const response = await createOrder(formData);
      const orderId = response.data?.order?.id;
      if (!orderId) throw new Error(t('order.noOrderId'));

      toast.success(t('order.createdSuccessfully'));
      setTimeout(() => {
        navigate(`/receipt/${orderId}`);
      }, 1500);
    } catch (err) {
      console.error("❌ Order creation error details:");
      console.error("Error:", err);
      console.error("Response:", err.response);

      // Use the improved error message from api.js
      const message = err.message || t('order.creationFailed');
      setError(message);
      toast.error(`❌ ${message}`);

      // Provide more specific guidance
      if (message.includes('prescription')) {
        toast.info(t('order.prescriptionRequiredHelp'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Get step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Person color="primary" />
                <Typography variant="h6" component="div">{t('order.customerInformation')}</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('order.customerName')}
                    fullWidth
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={t('order.exampleName')}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('order.customerPhone')}
                    fullWidth
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder={t('order.examplePhone')}
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('order.customerDetailsOptional')}
              </Alert>
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <LocalHospital color="primary" />
                  <Typography variant="h6" component="div">{t('order.medicines')}</Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => setOpenModal(true)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {t('order.addMedicine')}
                </Button>

                {items.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                          <TableCell><strong>{t('order.medicine')}</strong></TableCell>
                          <TableCell><strong>{t('order.category')}</strong></TableCell>
                          <TableCell><strong>{t('order.type')}</strong></TableCell>
                          <TableCell><strong>{t('order.price')}</strong></TableCell>
                          <TableCell><strong>{t('order.quantity')}</strong></TableCell>
                          <TableCell><strong>{t('order.total')}</strong></TableCell>
                          <TableCell><strong>{t('order.action')}</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02)
                              }
                            }}
                          >
                            <TableCell>
                              <Typography fontWeight="medium">
                                {item.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getCategoryIcon(item.category)}
                                <Chip
                                  label={t(`medicine.${item.category.toLowerCase()}`)}
                                  color={getCategoryColor(item.category)}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.requiresPrescription ? t('order.rxOnly') : t('order.otc')}
                                color={item.requiresPrescription ? 'error' : 'success'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{t('common.currency') || 'ETB'} {item.price.toFixed(2)}</TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                inputProps={{ min: 1 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" color="primary">
                                {t('common.currency') || 'ETB'} {(item.price * item.quantity).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRemoveMedicine(index)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <LocalHospital sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {t('order.noMedicinesAdded')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('order.clickToAddMedicine')}
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>

            {/* Prescription Photo Section */}
            {requiresPrescription && (
              <Card
                id="prescription-section"
                sx={{
                  border: `2px solid ${theme.palette.warning.main}`,
                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                  mb: 2
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Warning color="warning" />
                    <Typography variant="h6" color="warning.dark">
                      {t('order.prescriptionRequiredTitle')}
                    </Typography>
                  </Box>

                  {/* Show bypass status if already bypassed */}
                  {isPrescriptionBypassed ? (
                    <Alert
                      severity="success"
                      sx={{ mb: 2 }}
                      action={
                        <IconButton size="small" onClick={clearBypass}>
                          <Close />
                        </IconButton>
                      }
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {t('order.bypassApproved')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {t('order.reason')}: {bypassReason === 'other' ? bypassCustomReason : t(BYPASS_REASONS.find(r => r.id === bypassReason)?.label || '')}
                        </Typography>
                        {bypassApprovedBy && (
                          <Typography variant="caption" display="block">
                            {t('order.approvedBy')}: {bypassApprovedBy}
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {t('order.prescriptionRequiredText')}
                      </Typography>

                      {!photoPreview ? (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={cameraInputRef}
                            type="file"
                            capture="environment"
                            onChange={handleFileChange}
                          />
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={galleryInputRef}
                            type="file"
                            onChange={handleFileChange}
                          />

                          <Button
                            variant="contained"
                            startIcon={<PhotoCamera />}
                            onClick={openCamera}
                            color="warning"
                          >
                            {t('order.takePhoto')}
                          </Button>

                          <Button
                            variant="outlined"
                            startIcon={<Upload />}
                            onClick={openGallery}
                          >
                            {t('order.chooseFromGallery')}
                          </Button>

                          <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<AssignmentTurnedIn />}
                            onClick={openBypassDialog}
                          >
                            {t('order.bypassPrescription')}
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>{t('order.prescriptionPhoto')}:</Typography>
                          <Box
                            component="img"
                            src={photoPreview}
                            alt="Prescription"
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '200px',
                              borderRadius: 2,
                              border: `2px solid ${theme.palette.success.main}`,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              startIcon={<Close />}
                              onClick={clearPhoto}
                              color="error"
                              size="small"
                            >
                              {t('common.removePhoto')}
                            </Button>
                            <Button
                              startIcon={<AssignmentTurnedIn />}
                              onClick={openBypassDialog}
                              color="secondary"
                              size="small"
                            >
                              {t('order.bypassInstead')}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Cosmetics Tax Notice */}
            {hasCosmetics && (
              <Card sx={{ border: `2px solid ${theme.palette.info.main}`, bgcolor: alpha(theme.palette.info.main, 0.04) }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Spa color="info" />
                    <Typography variant="h6" color="info.dark">
                      {t('order.cosmeticsTaxNotice')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('order.cosmeticsTaxDescription')} ({(cosmeticsTaxRate * 100).toFixed(0)}%)
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 2:
        const cosmeticsItems = items.filter(item => item.category === 'Cosmetics');
        const hasCosmeticsInOrder = cosmeticsItems.length > 0;

        return (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Receipt color="primary" />
                <Typography variant="h6" component="div">{t('order.orderSummary')}</Typography>
              </Box>

              {/* Customer Info Summary */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('order.customerDetails')}
                </Typography>
                <Typography><strong>{t('order.name')}:</strong> {customerName || t('order.walkInCustomer')}</Typography>
                <Typography><strong>{t('order.phone')}:</strong> {customerPhone || t('order.notProvided')}</Typography>
              </Paper>

              {/* Prescription Status Summary */}
              {requiresPrescription && (
                <Paper sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: isPrescriptionBypassed ?
                    alpha(theme.palette.warning.main, 0.08) :
                    alpha(theme.palette.success.main, 0.08),
                  border: `1px solid ${isPrescriptionBypassed ? theme.palette.warning.main : theme.palette.success.main}`
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MedicalInformation color={isPrescriptionBypassed ? 'warning' : 'success'} />
                    <Typography variant="subtitle1" color={isPrescriptionBypassed ? 'warning.dark' : 'success.dark'}>
                      {isPrescriptionBypassed ? t('order.prescriptionBypassed') : t('order.prescriptionProvided')}
                    </Typography>
                  </Box>
                  {isPrescriptionBypassed ? (
                    <Box>
                      <Typography variant="body2">
                        <strong>{t('order.reason')}:</strong> {bypassReason === 'other' ? bypassCustomReason : t(BYPASS_REASONS.find(r => r.id === bypassReason)?.label || '')}
                      </Typography>
                      {bypassApprovedBy && (
                        <Typography variant="body2">
                          <strong>{t('order.approvedBy')}:</strong> {bypassApprovedBy}
                        </Typography>
                      )}
                      {bypassNotes && (
                        <Typography variant="body2">
                          <strong>{t('order.notes')}:</strong> {bypassNotes}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2">{t('order.prescriptionVerified')}</Typography>
                  )}
                </Paper>
              )}

              {/* Order Items Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('order.orderItems', { count: items.length })}
                </Typography>
                {items.map((item, index) => (
                  <Paper key={index} sx={{
                    p: 2,
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderLeft: item.category === 'Cosmetics' ? `4px solid ${theme.palette.info.main}` : 'none'
                  }}>
                    <Box>
                      <Typography fontWeight="medium">{item.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={t(`medicine.${item.category.toLowerCase()}`)}
                          color={getCategoryColor(item.category)}
                          size="small"
                          icon={getCategoryIcon(item.category)}
                        />
                        <Chip
                          label={item.requiresPrescription ? t('order.rxOnly') : t('order.otc')}
                          color={item.requiresPrescription ? 'error' : 'success'}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2">
                        {item.quantity} × {t('common.currency') || 'ETB'} {item.price.toFixed(2)}
                      </Typography>
                      <Typography fontWeight="bold" color="primary">
                        {t('common.currency') || 'ETB'} {(item.price * item.quantity).toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>

              {/* Cosmetics Tax Alert */}
              {hasCosmeticsInOrder && (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  icon={<Spa />}
                >
                  <Typography variant="body2">
                    {t('order.cosmeticsTaxApplied')} ({cosmeticsItems.length} {t('order.items')})
                  </Typography>
                </Alert>
              )}

              {/* Prescription Bypass Alert */}
              {isPrescriptionBypassed && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  icon={<AssignmentTurnedIn />}
                >
                  <Typography variant="body2">
                    {t('order.bypassWarning')}
                  </Typography>
                </Alert>
              )}

              {/* Total Section */}
              <Paper sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" component="div">
                    {t('order.totalAmount')}:
                  </Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {t('common.currency') || 'ETB'} {calculateTotal().toFixed(2)}
                  </Typography>
                </Box>
                {hasCosmeticsInOrder && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    {t('order.cosmeticsTaxNote')}
                  </Typography>
                )}
              </Paper>
            </CardContent>
          </Card>
        );

      default:
        return t('common.unknownStep');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: 4,
          py: 4,
          px: 3,
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}
      >
        <ShoppingCartCheckout sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          {t('order.createNewOrder')}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {t('order.stepByStepProcess')}
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{t(label)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <IconButton size="small" onClick={() => setError("")}>
            <Close />
          </IconButton>
        }>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      {getStepContent(activeStep)}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
          variant="outlined"
        >
          {t('common.back')}
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSubmit}
              disabled={loading || items.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              sx={{
                px: 4,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? t('common.processing') : t('order.completeOrder')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 1 && items.length === 0}
              endIcon={<CheckCircle />}
            >
              {activeStep === steps.length - 2 ? t('order.reviewOrder') : t('common.next')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Medicine Search Modal */}
      <MedicineSearchModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onAddMedicine={handleAddMedicine}
      />

      {/* Prescription Bypass Dialog */}
      <Dialog
        open={bypassDialogOpen}
        onClose={() => setBypassDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: alpha(theme.palette.warning.main, 0.04)
        }}>
          <AssignmentTurnedIn color="warning" />
          <Typography variant="h6" component="div" fontWeight="bold">
            {t('order.bypassPrescription')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('order.bypassWarningMessage')}
          </Alert>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('order.selectReason')}</InputLabel>
            <Select
              value={bypassReason}
              label={t('order.selectReason')}
              onChange={(e) => setBypassReason(e.target.value)}
            >
              {BYPASS_REASONS.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {t(reason.label)} - {t(reason.description)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {bypassReason === 'other' && (
            <TextField
              label={t('order.customReason')}
              fullWidth
              value={bypassCustomReason}
              onChange={(e) => setBypassCustomReason(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />
          )}

          <TextField
            label={t('order.approvedBy')}
            fullWidth
            value={bypassApprovedBy}
            onChange={(e) => setBypassApprovedBy(e.target.value)}
            placeholder={t('order.approvedByPlaceholder')}
            sx={{ mb: 3 }}
          />

          <TextField
            label={t('order.additionalNotes')}
            fullWidth
            value={bypassNotes}
            onChange={(e) => setBypassNotes(e.target.value)}
            multiline
            rows={3}
            placeholder={t('order.notesPlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setBypassDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleBypassSubmit}
            variant="contained"
            color="secondary"
            startIcon={<AssignmentTurnedIn />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.dark} 90%)`,
              boxShadow: '0 3px 15px rgba(156, 39, 176, 0.3)',
              '&:hover': {
                boxShadow: '0 5px 20px rgba(156, 39, 176, 0.4)',
              }
            }}
          >
            {t('order.approveBypass')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file inputs */}
      <input
        accept="image/*"
        style={{ display: 'none' }}
        ref={cameraInputRef}
        type="file"
        capture="environment"
        onChange={handleFileChange}
      />
      <input
        accept="image/*"
        style={{ display: 'none' }}
        ref={galleryInputRef}
        type="file"
        onChange={handleFileChange}
      />
    </Container>
  );
}